import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProgramStatus } from '@fluenceur/types';
import TopNav from '@/components/scaffold/top-nav/TopNav';
import { useProgramsCountQuery } from '@/hooks/queries/use-programs-count-query';

interface ProgramsStatusTabsProps {
  status: ProgramStatus;
  onStatusChange: (status: ProgramStatus) => void;
}

const STATUS_TABS = [
  {
    value: ProgramStatus.Active,
    labelKey: 'programs.browse.tabs.active',
    countLabelKey: 'programs.browse.tabs.activeWithCount',
  },
  {
    value: ProgramStatus.Inactive,
    labelKey: 'programs.browse.tabs.inactive',
    countLabelKey: 'programs.browse.tabs.inactiveWithCount',
  },
] as const;

const isStatusTab = (value: string): value is ProgramStatus =>
  STATUS_TABS.some((tab) => tab.value === value);

/**
 * Status navigation (Active / Inactive) using the app-wide `TopNav` tabs. It
 * sits at the top of the page, above the title, like the commissions page. The
 * selected status stays owned by the page (nuqs URL state); `TopNav` emits a
 * raw string, so it is narrowed back to a `ProgramStatus` here.
 *
 * Each tab carries its program count, like the mentions page. Counts come from
 * the dedicated count endpoint rather than the listing, so an unselected tab
 * does not pull a full program list with its stats and affiliate previews.
 */
const ProgramsStatusTabs = ({ status, onStatusChange }: ProgramsStatusTabsProps) => {
  const { t } = useTranslation();

  const { data: activeCount } = useProgramsCountQuery({ status: ProgramStatus.Active });
  const { data: inactiveCount } = useProgramsCountQuery({ status: ProgramStatus.Inactive });

  const counts = useMemo(
    () => ({
      [ProgramStatus.Active]: activeCount?.count,
      [ProgramStatus.Inactive]: inactiveCount?.count,
    }),
    [activeCount, inactiveCount]
  );

  const tabs = useMemo(
    () =>
      STATUS_TABS.map(({ value, labelKey, countLabelKey }) => {
        const count = counts[value];
        return {
          value,
          label: typeof count === 'number' ? t(countLabelKey, { count }) : t(labelKey),
        };
      }),
    [counts, t]
  );

  const handleTabChange = useCallback(
    (value: string) => {
      if (isStatusTab(value)) onStatusChange(value);
    },
    [onStatusChange]
  );

  return (
    <TopNav
      tabs={tabs}
      activeTab={status}
      onTabChange={handleTabChange}
      position="relative"
      bg="transparent"
      borderBottom="1px solid"
      borderColor="border"
    />
  );
};

export default ProgramsStatusTabs;
